import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Alert, App, Button, Col, Flex, Form, Modal, Row, Select, Switch } from 'antd';
import PropTypes from 'prop-types';
import {
  getExpenseExclusionOptions,
  previewExpenseExclusion,
  saveExpenseExclusion,
} from '../services/expense-exclusion';
import { expenseExclusionFields, normalizeExclusionDraft } from '../utils/expense-exclusion';
import ExpenseExclusionPreview from './ExpenseExclusionPreview';

const ExpenseExclusionEditor = ({ organizationId, rule, onClose, onSaved }) => {
  const [form] = Form.useForm();
  const { message } = App.useApp();
  const [preview, setPreview] = useState(null);
  const [pending, setPending] = useState(false);
  const [error, setError] = useState(null);
  const options = useQuery({
    queryKey: ['expense-exclusion-options', organizationId],
    queryFn: () => getExpenseExclusionOptions(organizationId),
  });
  const runPreview = async () => {
    setPreview(null);
    setError(null);
    try {
      const draft = normalizeExclusionDraft(await form.validateFields());
      setPending(true);
      const result = await previewExpenseExclusion(organizationId, draft);
      setPreview({ rule: draft, result });
    } catch (err) {
      setError(err.message || '请检查排除条件');
    } finally {
      setPending(false);
    }
  };
  const save = async () => {
    setPending(true);
    setError(null);
    try {
      await saveExpenseExclusion(organizationId, rule.id, preview.rule);
      message.success('费用排除规则已保存，下次计算时生效');
      onSaved();
    } catch (err) {
      setError(err.message || '保存失败，请重试');
    } finally {
      setPending(false);
    }
  };
  return (
    <Modal
      open
      title={rule.id ? '编辑费用排除规则' : '新增费用排除规则'}
      width={880}
      onCancel={onClose}
      closable={!pending}
      mask={{ closable: !pending }}
      keyboard={!pending}
      footer={
        <Flex gap="small" justify="end">
          <Button onClick={onClose} disabled={pending}>
            取消
          </Button>
          <Button
            onClick={runPreview}
            loading={pending}
            disabled={options.isPending || options.isError}
          >
            预览匹配
          </Button>
          <Button type="primary" onClick={save} disabled={!preview || pending}>
            确认保存
          </Button>
        </Flex>
      }
    >
      <Flex vertical gap="middle">
        <Alert
          type="info"
          showIcon
          message="同一规则的条件同时满足才排除；多条启用规则命中任一条即排除。留空表示不限制，至少选择一个条件。提单部门指流程发起人默认部门。"
        />
        {options.isError && (
          <Alert
            type="error"
            showIcon
            message="条件选项加载失败"
            action={<Button onClick={() => options.refetch()}>重试</Button>}
          />
        )}
        <Form
          form={form}
          layout="vertical"
          initialValues={{ ...rule, enabled: rule.enabled ?? true }}
          disabled={pending}
          onValuesChange={() => {
            setPreview(null);
            setError(null);
          }}
        >
          <Row gutter={16}>
            {expenseExclusionFields.map(({ name, label }) => (
              <Col key={name} xs={24} md={12}>
                <Form.Item name={name} label={label}>
                  <Select
                    allowClear
                    showSearch
                    optionFilterProp="label"
                    loading={options.isPending}
                    placeholder="不限制"
                    options={(options.data?.[name] || []).map((value) => ({ label: value, value }))}
                  />
                </Form.Item>
              </Col>
            ))}
          </Row>
          <Form.Item name="enabled" label="启用" valuePropName="checked">
            <Switch />
          </Form.Item>
        </Form>
        {error && <Alert type="error" showIcon message={error} />}
        {preview && <ExpenseExclusionPreview preview={preview.result} />}
      </Flex>
    </Modal>
  );
};
ExpenseExclusionEditor.propTypes = {
  organizationId: PropTypes.string.isRequired,
  rule: PropTypes.shape({ id: PropTypes.string, enabled: PropTypes.bool }).isRequired,
  onClose: PropTypes.func.isRequired,
  onSaved: PropTypes.func.isRequired,
};
export default ExpenseExclusionEditor;
